<script setup lang="ts">
import { computed } from 'vue'

import BaseDialog from '@/components/BaseModal.vue'

interface Props {
  isOpen: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info'
}

interface Emits {
  close: []
  confirm: []
  cancel: []
}

const props = withDefaults(defineProps<Props>(), {
  confirmText: 'Confirm',
  cancelText: undefined,
  variant: 'info',
})

const emit = defineEmits<Emits>()

const confirmButtonClass = computed(() => {
  const baseClasses = [
    'px-4',
    'py-2',
    'rounded-md',
    'text-sm',
    'font-medium',
    'text-white',
    'hover:cursor-pointer',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'transition-colors',
  ]

  switch (props.variant) {
    case 'danger':
      return [...baseClasses, 'bg-red-600', 'hover:bg-red-700', 'focus:ring-red-500']
    case 'warning':
      return [...baseClasses, 'bg-yellow-600', 'hover:bg-yellow-700', 'focus:ring-yellow-500']
    default:
      return [...baseClasses, 'bg-primary-600', 'hover:bg-primary-700', 'focus:ring-primary-500']
  }
})

const handleConfirm = () => {
  emit('confirm')
  emit('close')
}

const handleCancel = () => {
  emit('cancel')
  emit('close')
}

const handleClose = () => {
  emit('close')
}
</script>

<template>
  <BaseDialog
    :is-open="isOpen"
    :title="title"
    :description="`Confirmation dialog: ${message}`"
    size="sm"
    role="alertdialog"
    show-footer
    body-class="pb-2"
    @close="handleClose"
  >
    <!-- Message content -->
    <div class="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
      {{ message }}
    </div>

    <!-- Footer with action buttons -->
    <template #footer>
      <div class="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto">
        <button
          v-if="cancelText"
          @click="handleCancel"
          :class="[
            'w-full',
            'sm:w-auto',
            'px-4',
            'py-2',
            'border',
            'border-gray-300',
            'rounded-md',
            'text-sm',
            'font-medium',
            'text-gray-700',
            'bg-white',
            'hover:bg-gray-50',
            'hover:cursor-pointer',
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            'transition-colors',
          ]"
        >
          {{ cancelText }}
        </button>
        <button
          v-if="confirmText"
          @click="handleConfirm"
          :class="`w-full sm:w-auto ${confirmButtonClass.join(' ')}`"
        >
          {{ confirmText }}
        </button>
      </div>
    </template>
  </BaseDialog>
</template>
